import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../../../core/auth";
import { env } from "../../../../../env";
import useApi from "../../../../../shared/hooks/useApi";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import { useIndexedDraftList } from "../../../../entityProfileModal/hooks/useIndexedDraftList";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import {
  createEmptyContact,
  type EditPatientContact,
  getContactsValidationMessage,
  mapContactToEditContact,
  normalizeContact,
} from "./patientContacts.utils";

export function usePatientProfilePatientContactsController(patient: PatientProfilePatient) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const authState = auth.getAuthState();
  const canEdit = auth.isAllowedTo("edit_patient_page_information");
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const contacts = React.useMemo(
    () => (patient.contacts ?? []).filter((contact) => contact.isEmergencyContact === false),
    [patient.contacts]
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const draftContacts = useIndexedDraftList<EditPatientContact>();
  const [validationMessage, setValidationMessage] = React.useState<string | null>(null);
  const updateMainPhoneNumberMutation = useMutation({
    mutationFn: (mainPhoneNumber: string | null) =>
      api.patch("/agencies/:agencyId/patients/:patientId", {
        path: { patientId: patient.id },
        body: { id: patient.id, mainPhoneNumber },
      }),
    onSuccess: (_, mainPhoneNumber) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        return {
          ...currentPatient,
          mainPhoneNumber,
        };
      });
      toast({
        description:
          mainPhoneNumber === null ? "Main phone number removed." : "Main phone number updated.",
        status: "success",
      });
    },
    onError: () => {
      toast({ description: "Could not update main phone number.", status: "error" });
    },
  });
  const updateContactsMutation = useMutation({
    mutationFn: (contactsToSave: EditPatientContact[]) =>
      api.post("./patients/:patientId/contacts", {
        path: { patientId: patient.id },
        body: { contacts: contactsToSave },
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(["patient", "get", patient.id], (currentPatient: unknown) => {
        if (
          currentPatient === undefined ||
          currentPatient === null ||
          typeof currentPatient !== "object"
        ) {
          return currentPatient;
        }

        const emergencyContacts =
          (patient.contacts ?? []).filter((contact) => contact.isEmergencyContact === true) ?? [];

        return {
          ...currentPatient,
          contacts: [
            ...response.contacts.filter((contact) => contact.isEmergencyContact === false),
            ...emergencyContacts,
          ],
          familyInformation: response.contacts
            .filter((contact) => contact.isEmergencyContact === false)
            .map((contact) => ({
              firstName: contact.firstName,
              lastName: contact.lastName,
              relationship: contact.relationship,
              mobilePhoneNumber: contact.mobilePhoneNumber,
              homePhoneNumber: contact.homePhoneNumber,
            })),
        };
      });
      setIsEditing(false);
      setValidationMessage(null);
      toast({
        description: "Successfully updated contacts.",
        status: "success",
      });
    },
    onError: () => {
      toast({ description: "Could not update contacts.", status: "error" });
    },
  });

  const startEditing = () => {
    draftContacts.resetItems(contacts.map(mapContactToEditContact));
    setValidationMessage(null);
    setIsEditing(true);
  };
  const cancelEditing = () => {
    draftContacts.resetItems(contacts.map(mapContactToEditContact));
    setValidationMessage(null);
    setIsEditing(false);
  };
  const saveContacts = () => {
    const portalLoginPhoneRemovedIndex = draftContacts.items.findIndex((contact) => {
      const originalContact = contacts.find((item) => item.id === contact.id);

      return (
        originalContact?.isForPortalLogin === true &&
        (contact.mobilePhoneNumber ?? "").trim() === ""
      );
    });
    const nextValidationMessage =
      portalLoginPhoneRemovedIndex === -1
        ? getContactsValidationMessage(draftContacts.items)
        : `Can't delete phone number used for login on family member #${
            portalLoginPhoneRemovedIndex + 1
          }: mobile phone number`;

    if (nextValidationMessage !== null) {
      setValidationMessage(nextValidationMessage);
      return;
    }

    updateContactsMutation.mutate(draftContacts.items.map(normalizeContact));
  };
  const updateMainPhoneNumber = (nextMainPhoneNumber: string | null) => {
    const currentMainPhoneNumber =
      (patient as { mainPhoneNumber?: string | null }).mainPhoneNumber ?? null;
    const isRemoving = nextMainPhoneNumber === currentMainPhoneNumber;
    const replacingNumberString =
      currentMainPhoneNumber === null
        ? ""
        : ` from ${phoneFormatter.formatNationalIfValid(currentMainPhoneNumber)}`;
    const message = isRemoving
      ? "This patient's main phone will be deleted."
      : `This patient's main phone number will be changed${replacingNumberString} to ${phoneFormatter.formatNationalIfValid(
          nextMainPhoneNumber ?? ""
        )}.`;

    if (window.confirm(message) === false) {
      return;
    }

    updateMainPhoneNumberMutation.mutate(isRemoving ? null : nextMainPhoneNumber);
  };

  return {
    addDraftContact: () => draftContacts.addItem(createEmptyContact),
    canEdit,
    cancelEditing,
    contacts,
    draftContacts: draftContacts.items,
    isEditing,
    isOnboardingAgency,
    isSaving: updateContactsMutation.isPending || updateMainPhoneNumberMutation.isPending,
    removeDraftContact: draftContacts.removeItem,
    saveContacts,
    showErrors: validationMessage !== null,
    startEditing,
    updateDraftContact: draftContacts.updateItem,
    updateMainPhoneNumber,
    validationMessage,
  };
}
